/**
 * Lightweight client-side validators for form fields.
 *
 * Trade-offs:
 *  - Email + URL: standard, well-known regex bounds.
 *  - Phone: we do NOT pull in libphonenumber-js (~150 KB) to keep the
 *    bundle within budget. The regex below catches obvious typos and
 *    normalises to E.164-ish format. Real strict normalisation lives
 *    server-side when the integration with the carrier APIs lands.
 *  - Tax IDs: per-country regexes covering the LATAM + US footprint.
 *    Each rule is loose (counts + char-class) rather than checksum-
 *    based — we want to flag obvious mistakes, not reject legit edge
 *    cases. The server still does the canonical check.
 *
 * Every validator returns `null` on success or a translation-key
 * string the form layer can pass to `t()`.
 */

export type ValidationKey = string | null;

// ────────────────────────────────────────────────────────────────
// Email
// ────────────────────────────────────────────────────────────────
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,24}$/;

export function validateEmail(value: string | null | undefined): ValidationKey {
  if (!value || value.trim().length === 0) return null;
  return EMAIL_REGEX.test(value.trim()) ? null : 'validation.email.invalid';
}

// ────────────────────────────────────────────────────────────────
// Phone (loose E.164-ish)
// ────────────────────────────────────────────────────────────────
const PHONE_REGEX = /^[+]?[\d\s().-]{7,25}$/;

export function validatePhone(value: string | null | undefined): ValidationKey {
  if (!value || value.trim().length === 0) return null;
  return PHONE_REGEX.test(value.trim()) ? null : 'validation.phone.invalid';
}

/**
 * Best-effort normalisation to E.164: strip whitespace, parentheses,
 * dashes, dots. Doesn't insert a country code (the server picks the
 * canonical form via carrier).
 */
export function normalisePhone(value: string): string {
  const cleaned = value.replace(/[\s().-]/g, '');
  if (!cleaned.startsWith('+') && /^\d{8,}$/.test(cleaned)) {
    return cleaned;
  }
  return cleaned;
}

// ────────────────────────────────────────────────────────────────
// URL canonicalisation
// ────────────────────────────────────────────────────────────────
export function validateUrl(value: string | null | undefined): ValidationKey {
  if (!value || value.trim().length === 0) return null;
  try {
    const trimmed = value.trim();
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(withScheme);
    if (!parsed.hostname.includes('.')) return 'validation.url.invalid';
    return null;
  } catch {
    return 'validation.url.invalid';
  }
}

export function canonicaliseUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    parsed.hostname = parsed.hostname.toLowerCase();
    if (parsed.pathname === '/') parsed.pathname = '';
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return withScheme;
  }
}

// ────────────────────────────────────────────────────────────────
// Tax ID per country (loose checks; server does canonical)
// ────────────────────────────────────────────────────────────────
type TaxRule = { code: string; label: string; pattern: RegExp; example: string };

const TAX_RULES: Record<string, TaxRule[]> = {
  CO: [{ code: 'NIT', label: 'NIT', pattern: /^\d{8,11}(-\d)?$/, example: '900123456-7' }],
  MX: [
    { code: 'RFC', label: 'RFC', pattern: /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/i, example: 'ACM010101AB1' },
    { code: 'CURP', label: 'CURP', pattern: /^[A-Z]{4}\d{6}[A-Z]{6}[A-Z\d]{2}$/i, example: 'XXXX010101HDFXXX01' },
  ],
  CL: [{ code: 'RUT', label: 'RUT', pattern: /^\d{7,8}-[\dkK]$/, example: '12345678-9' }],
  PE: [{ code: 'RUC', label: 'RUC', pattern: /^(10|15|17|20)\d{9}$/, example: '20512345678' }],
  AR: [{ code: 'CUIT', label: 'CUIT', pattern: /^\d{2}-?\d{8}-?\d$/, example: '30-12345678-9' }],
  BR: [
    { code: 'CNPJ', label: 'CNPJ', pattern: /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/, example: '12.345.678/0001-90' },
    { code: 'CPF', label: 'CPF', pattern: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, example: '123.456.789-09' },
  ],
  US: [{ code: 'EIN', label: 'EIN', pattern: /^\d{2}-?\d{7}$/, example: '12-3456789' }],
  PR: [{ code: 'EIN', label: 'EIN', pattern: /^\d{2}-?\d{7}$/, example: '66-1234567' }],
  ES: [{ code: 'VAT', label: 'NIF/CIF', pattern: /^[A-Z]?\d{8}[A-Z\d]?$/i, example: 'A12345678' }],
  EC: [{ code: 'RUC', label: 'RUC', pattern: /^\d{13}$/, example: '0912345678001' }],
};

export interface TaxIdValidationResult {
  ok: boolean;
  detectedType?: string;
  errorKey?: string;
  example?: string;
}

/**
 * Validate a tax ID against the rules for the given country. If no
 * country is provided, returns ok=true (skip the check).
 */
export function validateTaxId(
  value: string | null | undefined,
  country: string | null | undefined,
  expectedType?: string,
): TaxIdValidationResult {
  if (!value || value.trim().length === 0) return { ok: true };
  if (!country) return { ok: true };
  const rules = TAX_RULES[country.toUpperCase()];
  if (!rules) return { ok: true }; // country with no rule yet — skip

  if (expectedType) {
    const rule = rules.find((r) => r.code === expectedType);
    if (!rule) return { ok: true };
    return rule.pattern.test(value.trim())
      ? { ok: true, detectedType: rule.code }
      : { ok: false, errorKey: `validation.taxId.invalid.${rule.code.toLowerCase()}`, example: rule.example };
  }

  const matched = rules.find((r) => r.pattern.test(value.trim()));
  if (matched) return { ok: true, detectedType: matched.code };
  return {
    ok: false,
    errorKey: 'validation.taxId.invalid.generic',
    example: rules.map((r) => `${r.label} (${r.example})`).join(' · '),
  };
}

export function taxIdTypesForCountry(country: string | null | undefined): string[] {
  if (!country) return [];
  return (TAX_RULES[country.toUpperCase()] ?? []).map((r) => r.code);
}

// ────────────────────────────────────────────────────────────────
// LinkedIn URL
// ────────────────────────────────────────────────────────────────
export function validateLinkedinUrl(value: string | null | undefined): ValidationKey {
  if (!value || value.trim().length === 0) return null;
  const ok = /linkedin\.com\/(in|company|sales|pub)\/[^\s/]+/i.test(value);
  return ok ? null : 'validation.linkedinUrl.invalid';
}

// ────────────────────────────────────────────────────────────────
// Domain extraction from email — used for account autosuggest
// ────────────────────────────────────────────────────────────────
export function extractDomain(email: string): string | null {
  const at = email.indexOf('@');
  if (at < 0) return null;
  const domain = email.substring(at + 1).toLowerCase().trim();
  // Skip generic personal mail providers
  const GENERIC = new Set([
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
    'icloud.com', 'me.com', 'proton.me', 'protonmail.com', 'aol.com',
  ]);
  if (GENERIC.has(domain)) return null;
  return domain || null;
}

// ────────────────────────────────────────────────────────────────
// Reference data
// ────────────────────────────────────────────────────────────────

export const COUNTRIES: ReadonlyArray<{ code: string; nameKey: string }> = [
  { code: 'CO', nameKey: 'reference.country.co' },
  { code: 'MX', nameKey: 'reference.country.mx' },
  { code: 'PR', nameKey: 'reference.country.pr' },
  { code: 'US', nameKey: 'reference.country.us' },
  { code: 'AR', nameKey: 'reference.country.ar' },
  { code: 'CL', nameKey: 'reference.country.cl' },
  { code: 'PE', nameKey: 'reference.country.pe' },
  { code: 'EC', nameKey: 'reference.country.ec' },
  { code: 'BR', nameKey: 'reference.country.br' },
  { code: 'ES', nameKey: 'reference.country.es' },
];

export const CURRENCIES: ReadonlyArray<string> = [
  'USD', 'COP', 'MXN', 'BRL', 'ARS', 'CLP', 'PEN', 'EUR', 'GBP',
];

export const LIFECYCLE_STAGES = [
  'PROSPECT', 'MQL', 'SQL', 'OPPORTUNITY', 'CUSTOMER', 'CHAMPION', 'CHURNED', 'DISQUALIFIED',
] as const;

export const REVENUE_BUCKETS = [
  'UNDER_1M', '1M_10M', '10M_50M', '50M_250M', '250M_1B', 'OVER_1B',
] as const;

export const SENIORITY_LEVELS = [
  'C_LEVEL', 'VP', 'DIRECTOR', 'MANAGER', 'INDIVIDUAL_CONTRIBUTOR', 'INTERN', 'BOARD', 'OWNER',
] as const;

export const DECISION_ROLES = [
  'DECISION_MAKER', 'CHAMPION', 'INFLUENCER', 'TECHNICAL_BUYER',
  'ECONOMIC_BUYER', 'USER', 'BLOCKER', 'UNKNOWN',
] as const;

export const PREFERRED_CHANNELS = [
  'EMAIL', 'WHATSAPP', 'SMS', 'CALL', 'LINKEDIN', 'IN_PERSON',
] as const;

export const DEAL_TYPES = [
  'NEW_BUSINESS', 'EXPANSION', 'RENEWAL', 'WIN_BACK', 'UPSELL', 'CROSS_SELL',
] as const;

export const CLOSE_REASONS = [
  'PRICE', 'TIMING', 'FEATURE_GAP', 'NO_BUDGET', 'COMPETITOR', 'NO_DECISION',
  'CHAMPION_LEFT', 'OUT_OF_SCOPE', 'BAD_FIT', 'WON_AS_SOLD', 'WON_DISCOUNTED', 'OTHER',
] as const;

export const PAYMENT_TERMS = [
  'PREPAID', 'NET_15', 'NET_30', 'NET_45', 'NET_60', 'NET_90',
  'MILESTONE', 'SUBSCRIPTION_MONTHLY', 'SUBSCRIPTION_ANNUAL', 'OTHER',
] as const;

export const ACQUISITION_CHANNELS = [
  'INBOUND', 'OUTBOUND', 'REFERRAL', 'PARTNER', 'EVENT', 'MARKETPLACE', 'EXPANSION', 'OTHER',
] as const;

export const DISQUALIFICATION_REASONS = [
  'NO_BUDGET', 'NO_AUTHORITY', 'NO_NEED', 'NO_TIMING', 'BAD_FIT', 'DUPLICATE',
  'COMPETITOR', 'UNRESPONSIVE', 'SPAM', 'TEST_DATA', 'OTHER',
] as const;

export const CAMPAIGN_OBJECTIVES = [
  'LEAD_GEN', 'AWARENESS', 'NURTURE', 'REACTIVATION', 'EVENT', 'PARTNER', 'EXPANSION', 'OTHER',
] as const;

export const CAMPAIGN_CHANNELS = [
  'EMAIL', 'SOCIAL', 'EVENTS', 'OUTBOUND', 'ADS', 'PARTNER', 'CONTENT', 'WEBINAR',
] as const;

/**
 * Common time zones grouped by primary deploy regions. Add more as
 * tenants land in new geos. The picker should also accept any IANA
 * value via free input.
 */
export const TIME_ZONES = [
  'America/Bogota', 'America/Mexico_City', 'America/Puerto_Rico',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles',
  'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Santiago',
  'America/Lima', 'America/Guayaquil', 'Europe/Madrid',
] as const;
